import type { CSSProperties, ReactNode } from 'react';
import type { BrandingSection, ContractPreset, PreviewDay } from '../wire';

/**
 * **A sketch, not the bundle.**
 *
 * State it plainly because the whole value of this pane depends on nobody
 * mistaking it for the thing it stands in for: `apps/site` is the authoritative
 * renderer. It ships its own `styles.css`, its own layout, its own type scale
 * and its own responsive rules, and none of that is here. What *is* here is the
 * one thing that transfers — the resolved token set, painted onto real CSS
 * custom properties (`--ys-color-accent`, exactly as the bundle names them) and
 * read by every rule below. So the colours, the typefaces, the radius and the
 * rhythm are true; the composition is an impression of one.
 *
 * Drift between this and the bundle is therefore **cosmetic by construction**.
 * A block that looks slightly different here than on the tenant's site is a
 * difference in this sketch, never a difference in what will be published — the
 * bytes are the same bytes, and the bundle is what renders them.
 *
 * ## Why there is no iframe
 *
 * The honest live preview is the bundle itself, in an iframe, painted with the
 * draft. It is not available: `apps/site` is a separate Vite build deployed to a
 * Cloud Storage bucket behind the load balancer on the *tenant's* hostname, it
 * takes its configuration from `/api/site` under a guest credential, and it has
 * no preview mode that accepts an unpublished document. Building one is a change
 * to the site bundle and to `guest-api`, which is a different piece of work; it
 * is carried as a residual rather than faked with a cross-origin frame that
 * would show the *published* row and quietly not be a preview at all.
 *
 * ## Why the services are placeholders
 *
 * The operator console cannot read a tenant's catalog. `items`,
 * `item_scheduling` and `resources` carry no grant for `admin_api` (migration
 * 0008), they are in `FORBIDDEN_NOUNS`, and `no-data-browser.test.ts` asserts
 * that reaching for one answers `42501`. Spec §6.4.3 asks for a preview "against
 * the tenant's real catalog" and spec §6.3 says this seat is "never a data
 * browser"; the grant is the enforceable one, so the services list is three
 * labelled placeholders and says so. The tenant's **name** and its **real
 * weekly hours** are not placeholders — both are the operator seat's to see, and
 * the hours are the same row the scheduling engine reads.
 */

export interface PreviewProps {
  name: string;
  preset: string;
  presets: ContractPreset[];
  overrides: Record<string, string>;
  sections: BrandingSection[];
  hours: PreviewDay[];
  siteTitle: string;
  tagline: string;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Three rows in the shape a spa's catalog has, and labelled as invented. */
const PLACEHOLDER_SERVICES = [
  { name: 'Sample treatment', duration: '60 min', price: '$120' },
  { name: 'Sample treatment', duration: '90 min', price: '$165' },
  { name: 'Sample treatment', duration: '30 min', price: '$70' },
];

export function resolveTokens(
  preset: string,
  presets: ContractPreset[],
  overrides: Record<string, string>,
): Record<string, string> {
  const base = presets.find((p) => p.name === preset)?.tokens ?? presets[0]?.tokens ?? {};
  const resolved: Record<string, string> = { ...base };
  for (const [name, value] of Object.entries(overrides)) {
    if (value !== '') resolved[name] = value;
  }
  return resolved;
}

/**
 * The resolved tokens as CSS custom properties, named the way the bundle names
 * them: `color.accent` → `--ys-color-accent`. Empty values are skipped rather
 * than written, for the bundle's own reason — an empty custom property makes
 * `var(--ys-image-hero, …)` skip its fallback.
 */
export function previewStyle(tokens: Record<string, string>): CSSProperties {
  const style: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens)) {
    if (value === '') continue;
    style[`--ys-${name.replace(/\./g, '-')}`] = value;
  }
  return style as unknown as CSSProperties;
}

export function BrandingPreview({
  name,
  preset,
  presets,
  overrides,
  sections,
  hours,
  siteTitle,
  tagline,
}: PreviewProps): ReactNode {
  const tokens = resolveTokens(preset, presets, overrides);
  const shown = sections.length === 0 ? [{ type: 'hero', slots: {} }] : sections;

  return (
    <div className="preview" style={previewStyle(tokens)} data-testid="branding-preview">
      <div className="preview__page">
        {shown.map((section, index) => (
          <Block
            key={`${section.type}-${String(index)}`}
            section={section}
            name={siteTitle === '' ? name : siteTitle}
            tagline={tagline}
            hours={hours}
          />
        ))}
      </div>
    </div>
  );
}

function Block({
  section,
  name,
  tagline,
  hours,
}: {
  section: BrandingSection;
  name: string;
  tagline: string;
  hours: PreviewDay[];
}): ReactNode {
  const slot = (key: string): string => {
    const value = section.slots[key];
    return typeof value === 'string' ? value : '';
  };
  const flag = (key: string, fallback: boolean): boolean => {
    const value = section.slots[key];
    return typeof value === 'boolean' ? value : fallback;
  };

  switch (section.type) {
    case 'hero':
      return (
        <header className={`preview__hero preview__hero--${slot('align') || 'left'}`}>
          {/* The tenant's own name is the honest fallback headline — it comes
              from the tenant row, not from config, exactly as it does in the
              bundle, so a site with no branding still says who it is. */}
          <h1 className="preview__headline">{slot('headline') === '' ? name : slot('headline')}</h1>
          {slot('sub') === '' ? (
            tagline === '' ? null : (
              <p className="preview__sub">{tagline}</p>
            )
          ) : (
            <p className="preview__sub">{slot('sub')}</p>
          )}
          <span className="preview__cta">{slot('cta_label') || 'Book a time'}</span>
        </header>
      );

    case 'services':
      return (
        <section className="preview__section">
          <h2 className="preview__title">{slot('headline') || 'What we offer'}</h2>
          {slot('note') === '' ? null : <p className="preview__note">{slot('note')}</p>}
          <ul className={`preview__services preview__services--${slot('layout') || 'list'}`}>
            {PLACEHOLDER_SERVICES.map((service, i) => (
              <li key={i}>
                <span>{service.name}</span>
                <span className="preview__meta">
                  {flag('show_duration', true) ? service.duration : ''}
                  {flag('show_price', true) ? ` · ${service.price}` : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="preview__placeholder">
            Placeholder rows. The console holds no grant on this tenant’s catalog (spec §6.3).
          </p>
        </section>
      );

    case 'hours':
      return (
        <section className="preview__section">
          <h2 className="preview__title">{slot('headline') || 'Hours'}</h2>
          {/* The tenant's real week, from the same row the scheduling engine
              reads — so a page advertising hours the book no longer keeps is
              visible here before it is visible to a guest. */}
          <ul className="preview__hours">
            {hours.length === 0 ? (
              <li>
                <span>No hours published</span>
              </li>
            ) : (
              hours.map((day) => (
                <li key={day.weekday}>
                  <span>{DAYS[day.weekday] ?? String(day.weekday)}</span>
                  <span className="preview__meta">
                    {day.closed || day.windows.length === 0
                      ? 'Closed'
                      : day.windows.map((w) => `${w.start}–${w.end}`).join(', ')}
                  </span>
                </li>
              ))
            )}
          </ul>
          {slot('note') === '' ? null : <p className="preview__note">{slot('note')}</p>}
        </section>
      );

    case 'booking':
      return (
        <section className="preview__section preview__booking">
          <h2 className="preview__title">{slot('headline') || 'Book a time'}</h2>
          {slot('note') === '' ? null : <p className="preview__note">{slot('note')}</p>}
          <span className="preview__cta">Choose a time</span>
        </section>
      );

    default:
      return (
        <section className="preview__section">
          <h2 className="preview__title">{slot('headline') || section.type}</h2>
          {/*
           * Content zones are shown as their **text**, never as markup.
           *
           * The console deliberately owns no `dangerouslySetInnerHTML`: the
           * bundle's `RichText` is the one component in the system that renders
           * a zone, and a second one here would be a second place trusting the
           * same bytes. The editor shows the exact sanitized bytes in its own
           * box, which is the part that teaches; this is the part that shows the
           * shape of the page.
           */}
          <p className="preview__note">{textOf(section)}</p>
        </section>
      );
  }
}

/** Tags stripped for display. Not a sanitizer — the bytes are already sanitized. */
function textOf(section: BrandingSection): string {
  for (const key of ['body_html', 'body', 'note', 'html']) {
    const value = section.slots[key];
    if (typeof value === 'string' && value !== '') {
      return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
  }
  return '—';
}
